import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Skeleton,
  Alert,
  Stack,
  IconButton,
} from '@mui/material';
import { Badge, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { esocialService } from '../../../services/esocialService';
import type { ESocialEvent } from '../../../types/esocial';
import {
  ESocialEventStatus,
  ESOCIAL_EVENT_TYPE_SHORT_LABELS,
  ESOCIAL_STATUS_LABELS,
  getEventStatusColor,
} from '../../../types/esocial';

interface EmployeeCardProps {
  employeeId: number;
  companyId: number;
}

const ACCENT = '#4527A0';
const PAGE_SIZE = 5;

export const EmployeeESocialEventsCard: React.FC<EmployeeCardProps> = ({ employeeId, companyId }) => {
  const [events, setEvents] = useState<ESocialEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    esocialService
      .getEvents(String(companyId), {
        limit: 1000,
        sortBy: 'eventDate',
        sortOrder: 'DESC',
        filters: { employeeId },
      })
      .then((res) => {
        if (cancelled) return;
        setEvents(res.events || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Erro ao carregar eventos e-Social');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [employeeId, companyId]);

  const statusCounts = events.reduce<Record<string, number>>((acc, e) => {
    acc[e.eventStatus] = (acc[e.eventStatus] || 0) + 1;
    return acc;
  }, {});

  const formatDate = (d: string) => {
    try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; }
  };

  const totalPages = Math.ceil(events.length / PAGE_SIZE);
  const paged = events.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Card variant="outlined" sx={{ height: '100%', borderTop: `3px solid ${ACCENT}` }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box sx={{ color: ACCENT, display: 'flex' }}><Badge fontSize="small" /></Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            Eventos e-Social
          </Typography>
          {!loading && !error && (
            <Chip label={events.length} size="small" sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700 }} />
          )}
        </Box>

        {/* Body */}
        {loading && (
          <Stack spacing={1}>
            {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={32} />)}
          </Stack>
        )}

        {error && <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>{error}</Alert>}

        {!loading && !error && events.length === 0 && (
          <Typography variant="body2" color="text.secondary">Nenhum registro encontrado</Typography>
        )}

        {!loading && !error && events.length > 0 && (
          <>
            {/* Summary chips */}
            <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5}>
              {Object.entries(statusCounts).map(([status, count]) => (
                <Chip
                  key={status}
                  label={`${ESOCIAL_STATUS_LABELS[status as ESocialEventStatus] || status}: ${count}`}
                  size="small"
                  color={getEventStatusColor(status as ESocialEventStatus)}
                  variant="outlined"
                />
              ))}
            </Box>

            {/* List */}
            <Stack spacing={0.75}>
              {paged.map((ev) => (
                <Box
                  key={ev.id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: 'action.hover' }}
                >
                  <Typography variant="caption" fontWeight={600} sx={{ minWidth: 48 }}>
                    {ESOCIAL_EVENT_TYPE_SHORT_LABELS[ev.eventType] || ev.eventType}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    {formatDate(ev.eventDate)}
                  </Typography>
                  <Chip
                    label={ESOCIAL_STATUS_LABELS[ev.eventStatus] || ev.eventStatus}
                    size="small"
                    color={getEventStatusColor(ev.eventStatus)}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              ))}
            </Stack>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} mt={1}>
                <Typography variant="caption" color="text.secondary">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, events.length)} de {events.length}
                </Typography>
                <IconButton size="small" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
